import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Modifier {
  modifierId: string;
  name: string;
  [key: string]: any;
}

interface Product {
  productId: string;
  [key: string]: any;
}

interface Menu {
  modifiers?: Modifier[];
  [key: string]: any;
}

interface Slot {
  slotId: string;
  modifiers?: any[];
  [key: string]: any;
}

export interface ModifierSelectProps {
  value: Modifier | null | Modifier[];
  onChange: (modifier: Modifier | null | Modifier[]) => void;
  product?: Product | null;
  menu?: Menu | null;
  slot?: Slot | null;
  multiple?: boolean;
  /** When provided, bypasses internal derivation and uses these modifiers directly */
  modifiers?: Modifier[];
}

export function ModifierSelect({
  value,
  onChange,
  product,
  menu,
  slot,
  multiple = false,
  modifiers: externalModifiers,
}: ModifierSelectProps) {
  const [modifiers, setModifiers] = useState<Modifier[]>([]);
  const [modifiersLoading, setModifiersLoading] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // When external modifiers are provided, use them directly and auto-clear invalid selections
  useEffect(() => {
    if (externalModifiers === undefined) return;
    setModifiers(externalModifiers);
    if (multiple && Array.isArray(value)) {
      const valid = value.filter(v => externalModifiers.some(m => m.modifierId === v.modifierId));
      if (valid.length !== value.length) {
        onChangeRef.current(valid);
      }
    }
  }, [externalModifiers, multiple]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalModifiers !== undefined) return;

    setModifiersLoading(true);

    if (slot && slot.modifiers && Array.isArray(slot.modifiers)) {
      const slotModifiers = slot.modifiers.map((m: any) => m.modifier).filter(Boolean);
      setModifiers(slotModifiers);
      setModifiersLoading(false);
      return;
    }

    if (!product && menu && menu.modifiers && Array.isArray(menu.modifiers)) {
      setModifiers(menu.modifiers);
      setModifiersLoading(false);
      return;
    }

    if (product && product.variants && Array.isArray(product.variants)) {
      const selectedVariant = product.selectedVariant || product.variants[0];

      if (selectedVariant && selectedVariant.slots && Array.isArray(selectedVariant.slots)) {
        const allModifiers: Modifier[] = [];
        selectedVariant.slots.forEach((slotItem: any) => {
          (slotItem.modifiers ?? []).forEach((modifierItem: any) => {
            if (modifierItem.modifier) allModifiers.push(modifierItem.modifier);
          });
        });
        const uniqueModifiers = allModifiers.filter((m, i, a) => a.findIndex(x => x.modifierId === m.modifierId) === i);
        setModifiers(uniqueModifiers);
      } else {
        setModifiers([]);
      }
    } else {
      setModifiers([]);
    }

    setModifiersLoading(false);
  }, [product, menu, slot, externalModifiers]);

  return (
    <Autocomplete
      options={Array.isArray(modifiers) ? modifiers : []}
      disableCloseOnSelect
      filterSelectedOptions
      getOptionLabel={(option: Modifier) => {
        if (!option) return 'No modifier';
        if (!option.name && !option.modifierCode) return 'Unknown';
        return `${option.name ?? 'Unknown'} (${option.modifierCode ?? 'Unknown'})`;
      }}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.modifierId === (val as Modifier).modifierId;
      }}
      renderOption={(props, option) => {
        if (!option) return null;
        const label = (!option.name && !option.modifierCode)
          ? 'Unknown'
          : `${option.name ?? 'Unknown'} (${option.modifierCode ?? 'Unknown'})`;
        return (
          <li {...props} key={option.modifierId || `modifier-${Math.random()}`}>
            {label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        if (!tagValue || !Array.isArray(tagValue)) return [];
        return (tagValue as Modifier[]).map((option, index) => {
          if (!option) return null;
          const label = (!option.name && !option.modifierCode)
            ? 'Unknown'
            : `${option.name ?? 'Unknown'} (${option.modifierCode ?? 'Unknown'})`;
          return (
            <Chip
              {...getTagProps({ index })}
              key={option.modifierId || `modifier-tag-${index}`}
              size="small"
              label={label}
            />
          );
        });
      }}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      multiple={multiple}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Modifier"
          placeholder="Search for modifiers..."
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {modifiersLoading && <CircularProgress data-testid="loading-indicator" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
